/**
 * @file Transforms domain events into Traxex event format for POSTing to /qqms-event endpoint.
 *
 * IMPORTANT: These functions return TraxexSurveyEvent types (not BQSurveyEvent).
 *
 * The TraxexSurveyEvent types represent what we SEND TO Traxex. After server-side
 * enrichment, Traxex stores events as BQSurveyEvent in BigQuery.
 *
 * KEY DIFFERENCES:
 * - Geography is supplied by the QQMS delivery adapter
 * - event_id is OPTIONAL - Traxex generates a UUID if not provided
 * - event_timestamp will be OVERRIDDEN - Traxex uses server time
 * - user.user_agent will be OVERRIDDEN - Traxex extracts from HTTP headers
 *
 * See libs/qqms/types/src/lib/traxex-events.ts for full type documentation.
 */

import type {
    AnswerEvent,
    CardEvent,
    ExternalEventContext,
    FreeformLinearAnswer,
    SurveyEvent,
    TraxexAnswer,
    TraxexAnswerSubmittedEvent,
    TraxexCard,
    TraxexCardType,
    TraxexCardViewedEvent,
    TraxexCompatibleCard,
    TraxexContent,
    TraxexDevice,
    TraxexDSP,
    TraxexGeography,
    TraxexQuestionSkippedEvent,
    TraxexSurveyCompletedEvent,
    TraxexSurveyEvent,
    TraxexSurveyViewedEvent,
    TraxexTitleInteractedEvent,
    TraxexUser,
} from '@dmp/qqms/types';
import {
    detectPlatform,
    getAdOrientation,
    getScreenSize,
} from '@dmp/shared/ad-utils';
import { isLinearQuestionFromCoreQuestion } from './type-guards';

// ----------------------------------------------------------------
// Survey Events
// ----------------------------------------------------------------

/**
 * Creates a TraxexSurveyViewedEvent for sending to Traxex.
 * Fired when the survey first loads/becomes visible.
 */
export const transformToSurveyViewed = (
    surveyEvent: SurveyEvent
): TraxexSurveyViewedEvent =>
    buildSurveyTraxexEvent('survey_viewed', surveyEvent);

/**
 * Creates a TraxexSurveyCompletedEvent for sending to Traxex.
 * Fired when the user completes the survey by reaching a terminal card.
 */
export const transformToSurveyCompleted = (
    surveyEvent: SurveyEvent
): TraxexSurveyCompletedEvent =>
    buildSurveyTraxexEvent('survey_completed', surveyEvent);

const buildSurveyTraxexEvent = <
    T extends TraxexSurveyViewedEvent | TraxexSurveyCompletedEvent
>(
    eventType: T['event_type'],
    surveyEvent: SurveyEvent
): T => {
    const { survey, eventContext, containerSize, sessionId } = surveyEvent;
    const commonContext = buildCommonContext(
        sessionId,
        survey,
        eventContext,
        containerSize
    );

    return {
        event_id: crypto.randomUUID(),
        event_type: eventType,
        ...commonContext,
    } as T;
};

// ----------------------------------------------------------------
// Card Events
// ----------------------------------------------------------------

/**
 * Transforms a CardEvent to a TraxexCardViewedEvent for sending to Traxex.
 * Note: event_timestamp is not included; Traxex fills it server-side.
 */
export const transformCardEventToCardViewed = (
    cardEvent: CardEvent
): TraxexCardViewedEvent => buildCardTraxexEvent('card_viewed', cardEvent);

/**
 * Transforms a CardEvent with a skip suffix to a TraxexQuestionSkippedEvent for sending to Traxex.
 * Note: event_timestamp is not included; Traxex fills it server-side.
 */
export const transformCardEventToQuestionSkipped = (
    cardEvent: CardEvent
): TraxexQuestionSkippedEvent =>
    buildCardTraxexEvent('question_skipped', cardEvent);

/**
 * Creates a TraxexTitleInteractedEvent for sending to Traxex.
 * Fired when the user clicks the button on a title card.
 */
export const transformCardEventToTitleInteracted = (
    cardEvent: CardEvent
): TraxexTitleInteractedEvent =>
    buildCardTraxexEvent('title_interacted', cardEvent);

/**
 * Build Traxex card event, which only differ in shape by event_type.
 */
const buildCardTraxexEvent = <
    T extends
        | TraxexCardViewedEvent
        | TraxexQuestionSkippedEvent
        | TraxexTitleInteractedEvent
>(
    eventType: T['event_type'],
    cardEvent: CardEvent
): T => {
    const { card, survey, eventContext, containerSize, sessionId } = cardEvent;

    const commonContext = buildCommonContext(
        sessionId,
        survey,
        eventContext,
        containerSize
    );

    return {
        event_id: crypto.randomUUID(),
        event_type: eventType,
        card: buildCardInfo(card),
        ...commonContext,
    } as T;
};

// ----------------------------------------------------------------
// Answer Events
// ----------------------------------------------------------------

/**
 * Transforms an AnswerEvent to a TraxexAnswerSubmittedEvent for sending to Traxex.
 * Note: event_timestamp is not included; Traxex fills it server-side.
 */
export const transformAnswerEventToAnswerSubmitted = (
    answerEvent: AnswerEvent
): TraxexAnswerSubmittedEvent => {
    const {
        question,
        answers,
        survey,
        eventContext,
        containerSize,
        sessionId,
    } = answerEvent;

    const commonContext = buildCommonContext(
        sessionId,
        survey,
        eventContext,
        containerSize
    );

    const traxexAnswers: TraxexAnswer[] = answers.map((answer) => {
        const freeformAnswer =
            'isFreeForm' in answer && answer.isFreeForm === true
                ? (answer as FreeformLinearAnswer)
                : undefined;

        return {
            id: answer.id,
            label: answer.traxexLabel,
            free_form_value: freeformAnswer?.userInput,
        };
    });

    const event: TraxexAnswerSubmittedEvent = {
        // Optional - Traxex generates UUID if not provided
        event_id: crypto.randomUUID(),
        // event_timestamp is OMITTED - Traxex uses server time
        event_type: 'answer_submitted',
        ...commonContext,
        card: buildCardInfo(question),
        answers: traxexAnswers,
    };

    return event;
};

// ----------------------------------------------------------------
// Utilities
// ----------------------------------------------------------------

/**
 * Builds the common context shared by all event types for Traxex.
 * Returns fields that will be sent to Traxex.
 *
 * @param containerSize - The measured dimensions of the survey container element,
 *   provided by the renderer at the time of event dispatch. This is the outer wrapper
 *   div (Shadow Root host) which is in the normal DOM, avoiding Shadow DOM piercing issues.
 */
const buildCommonContext = (
    sessionId: string,
    survey: SurveyEvent['survey'],
    eventContext: ExternalEventContext,
    containerSize: { width: number; height: number }
): Pick<
    TraxexSurveyEvent,
    | 'session_id'
    | 'survey_id'
    | 'survey_version_id'
    | 'survey_label'
    | 'user'
    | 'content'
    | 'device'
    | 'dsp'
    | 'geography'
> => {
    const screenSize = getScreenSize(); // Physical device screen dimensions

    const user: TraxexUser | undefined = eventContext.gup_id
        ? {
              id: eventContext.gup_id,
              subscription_status: eventContext.user_type || undefined,
              is_bot: !!eventContext.bot,
              bot_name: eventContext.bot || undefined,
              // user_agent is intentionally OMITTED - Traxex fills from HTTP headers
          }
        : undefined;

    const content: TraxexContent = {
        site_domain: eventContext.site,
        site_code: eventContext.sc,
        url: window.location.href,
        content_id: eventContext.cid || undefined,
        content_type: eventContext.ctype || undefined,
        ssts:
            eventContext.ssts_s || eventContext.ssts_ss || eventContext.ssts_t
                ? {
                      section: eventContext.ssts_s || undefined,
                      subsection: eventContext.ssts_ss || undefined,
                      topic: eventContext.ssts_t || undefined,
                  }
                : undefined,
    };

    // Build device context
    // - screen_size: physical device screen via getScreenSize() (window.screen with SafeFrame fallback)
    //   Note: SafeFrame restricts access to true physical screen for privacy
    // - viewport_size: survey container size measured at dispatch time by the renderer.
    //   The renderer measures the outer wrapper div (Shadow Root host) which is in the
    //   normal DOM, so getBoundingClientRect() works without Shadow DOM piercing.
    const device: TraxexDevice = {
        platform: detectPlatform(eventContext.package_name),
        orientation: getAdOrientation(containerSize),
        screen_size: {
            width: screenSize.width,
            height: screenSize.height,
        },
        viewport_size: {
            width: containerSize.width,
            height: containerSize.height,
        },
    };

    const geography: TraxexGeography | undefined = eventContext.geography
        ? {
              country: eventContext.geography.country,
              region: eventContext.geography.region,
              city: eventContext.geography.city,
              postal_code: eventContext.geography.postalCode,
          }
        : undefined;

    const dsp: TraxexDSP | undefined =
        eventContext.gam_advertiser ||
        eventContext.gam_order ||
        eventContext.gam_line ||
        eventContext.gam_creative
            ? {
                  platform: 'gam',
                  advertiser_id: eventContext.gam_advertiser,
                  order_id: eventContext.gam_order,
                  line_item_id: eventContext.gam_line,
                  creative_id: eventContext.gam_creative,
              }
            : undefined;

    return {
        session_id: sessionId,
        survey_id: survey.id,
        survey_version_id: survey.versionId,
        survey_label: survey.traxexLabel,
        user,
        content,
        device,
        geography,
        dsp,
    };
};

/**
 * Builds card information for Traxex.
 */
const buildCardInfo = (card: TraxexCompatibleCard): TraxexCard => {
    const cardType = determineCardType(card);
    const isCoreQuestion = isLinearQuestionFromCoreQuestion(card);
    const isForked = 'type' in card && card.type === 'forkedQuestion';

    return {
        id: card.id,
        type: cardType,
        label: card.traxexLabel,
        question:
            cardType === 'question'
                ? {
                      is_forked: !!isForked,
                      is_core_question: isCoreQuestion,
                  }
                : undefined,
    };
};

/**
 * Determines the card type for Traxex/BigQuery based on the card.
 */
const determineCardType = (card: TraxexCompatibleCard): TraxexCardType => {
    // Handle different card types
    if ('type' in card) {
        switch (card.type) {
            case 'linearQuestion':
            case 'forkedQuestion':
                return 'question';
            case 'titleCard':
                return 'title';
            case 'thankYouCard':
                return 'thankyou';
            case 'pollResultCard':
                return 'pollresult';
            default:
                card satisfies never;
                return 'question';
        }
    }

    return 'question';
};
